import { Button, Modal, Callout } from '@dev-spendesk/grapes';
import cx from 'classnames';
import { useReducer, useState, type Reducer } from 'react';

import { RadioListSelector } from 'src/core/common/components/RadioListSelector/RadioListSelector';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';

import IllustrationImport from './illustration-import.svg';
import IllustrationManual from './illustration-manual.svg';

import './CostCenterCreationMethodSelectionModal.css';

type CostCenterCreationType = 'import' | 'manual';

type Props = {
  onSelect(type: CostCenterCreationType): void;
  onCancel(): void;
  isOpen: boolean;
  isMapSyncEnabled: boolean;
};

type Action = 'selectImport' | 'selectManual' | 'showError';

type State = {
  method: CostCenterCreationType | undefined;
  error: boolean;
};

const reducer: Reducer<State, Action> = (state, action) => {
  switch (action) {
    case 'selectImport':
      return { method: 'import', error: false };
    case 'selectManual':
      return { method: 'manual', error: false };
    case 'showError':
      return { method: undefined, error: true };
    default:
      return state;
  }
};

type InfoProps = {
  onChange: (action: Action) => void;
};

const CostCenterCreationMethodSelectionWithInfo = ({ onChange }: InfoProps) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const [method, setMethod] = useState<string | null>(null);

  const handleChange = (value: string) => {
    setMethod(value);

    switch (value) {
      case 'import':
        onChange('selectImport');
        break;
      case 'manual':
        onChange('selectManual');
        break;
      default:
        onChange('showError');
        break;
    }
  };

  return (
    <RadioListSelector
      value={method}
      name="costCenterCreationType"
      onChange={handleChange}
      options={[
        {
          label: t(
            'costCenters.creationMethodSelection.integrationInfo.manual.title',
          ),
          iconName: 'link',
          iconVariant: 'forest',
          value: 'manual',
          notes: [
            t(
              'costCenters.creationMethodSelection.integrationInfo.manual.helpText.line1',
            ),
            t(
              'costCenters.creationMethodSelection.integrationInfo.manual.helpText.line2',
              { integration: accountingSoftwareName },
            ),
          ],
        },
        {
          label: t(
            'costCenters.creationMethodSelection.integrationInfo.import.title',
          ),
          iconName: 'arrow-down-tray',
          iconVariant: 'pink',
          value: 'import',
          notes: [
            t(
              'costCenters.creationMethodSelection.integrationInfo.import.helpText.line1',
            ),
            t(
              'costCenters.creationMethodSelection.integrationInfo.import.helpText.line2',
              { integration: accountingSoftwareName },
            ),
          ],
        },
      ]}
    />
  );
};

export const CostCenterCreationMethodSelectionModal = ({
  onSelect,
  onCancel,
  isOpen,
  isMapSyncEnabled,
}: Props) => {
  const { t } = useTranslation('global');

  const [{ method, error }, dispatch] = useReducer(reducer, {
    method: undefined,
    error: false,
  });

  return (
    <Modal
      isOpen={isOpen}
      iconName="circle-plus"
      iconVariant="info"
      title={t('costCenters.creationMethodSelection.title')}
      subtitle={
        !isMapSyncEnabled
          ? t('costCenters.creationMethodSelection.subtitle')
          : undefined
      }
      actions={[
        <Button
          key="cancel-button"
          onClick={() => onCancel()}
          text={t('misc.cancel')}
          variant="secondaryNeutral"
        />,
        <Button
          key="continue-button"
          text={t(`misc.continue`)}
          iconName="chevron-right"
          iconPosition="right"
          variant="primaryBrand"
          onClick={() => {
            if (!method) {
              return dispatch('showError');
            }

            onSelect(method);
          }}
        />,
      ]}
    >
      {isMapSyncEnabled ? (
        <CostCenterCreationMethodSelectionWithInfo onChange={dispatch} />
      ) : (
        <>
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => dispatch('selectManual')}
              className={cx(
                'CostCenterCreationMethodSelectionModal__selection',
                {
                  selected: method === 'manual',
                },
              )}
            >
              <img
                width="119"
                height="95"
                alt=""
                src={IllustrationManual}
                className="my-24"
              />
              <h1 className="text-content-primary title-m">
                {t('costCenters.creationMethodSelection.manual.title')}
              </h1>
              <p className="body-m mx-16 mb-24">
                {t('costCenters.creationMethodSelection.manual.subtitle')}
              </p>
            </button>
            <button
              type="button"
              onClick={() => dispatch('selectImport')}
              className={cx(
                'CostCenterCreationMethodSelectionModal__selection',
                {
                  selected: method === 'import',
                },
              )}
            >
              <img
                width="128"
                height="95"
                alt=""
                src={IllustrationImport}
                className="my-24"
              />
              <h1 className="text-content-primary title-m">
                {t('costCenters.creationMethodSelection.import.title')}
              </h1>
              <p className="body-m mx-16 mb-24">
                {t('costCenters.creationMethodSelection.import.subtitle')}
              </p>
            </button>
          </div>
        </>
      )}

      {error && (
        <Callout
          className="mt-16"
          variant="alert"
          title={t('costCenters.creationMethodSelection.error')}
        />
      )}
    </Modal>
  );
};
